import React from "react";
import { useNavigate } from "react-router-dom";

export default function SalesPurchaseHub() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Sales Operations",
      items: [
        {
          title: "Quotations",
          description: "Draft and review sales quotes and price estimates for client approval.",
          route: "/admin/quotations",
          icon: "request_quote",
          color: "bg-blue-50 text-blue-600 border-blue-100",
          badge: "12 Pending",
          badgeColor: "bg-blue-100/50 text-blue-700",
        },
        {
          title: "Proforma Invoices",
          description: "Generate preliminary pre-shipment invoices for import/export and sales verification.",
          route: "/admin/performa-invoices",
          icon: "description",
          color: "bg-indigo-50 text-indigo-600 border-indigo-100",
          badge: "04 Drafts",
          badgeColor: "bg-indigo-100/50 text-indigo-700",
        },
        {
          title: "Sales Invoices",
          description: "Access and create GST-compliant tax invoices for customer accounts.",
          route: "/admin/sales-invoices",
          icon: "receipt_long",
          color: "bg-emerald-50 text-emerald-600 border-emerald-100",
          badge: "₹2.4M Owed",
          badgeColor: "bg-emerald-100/50 text-emerald-700",
        },
      ],
    },
    {
      title: "Purchases",
      items: [
        {
          title: "Purchase Orders (PO)",
          description: "Draft formal procurement purchase requests issued to material vendors.",
          route: "/admin/purchase-orders",
          icon: "shopping_cart",
          color: "bg-amber-50 text-amber-600 border-amber-100",
        },
        {
          title: "Purchase Invoices",
          description: "Record supplier bills, verify input tax credits, and process raw materials payments.",
          route: "/admin/purchase-invoices",
          icon: "account_balance_wallet",
          color: "bg-cyan-50 text-cyan-600 border-cyan-100",
        },
      ],
    },
    {
      title: "Delivery Challans (DC)",
      items: [
        {
          title: "Returnable DC",
          description: "Manage outgoing material challans expected to return back to warehouse.",
          route: "/admin/delivery-challans?type=returnable",
          icon: "local_shipping",
          color: "bg-orange-50 text-orange-600 border-orange-100",
        },
        {
          title: "Billing DC",
          description: "Process outbound challans for billing material and equipment deliveries.",
          route: "/admin/delivery-challans?type=non_returnable",
          icon: "local_shipping",
          color: "bg-rose-50 text-rose-600 border-rose-100",
        },
        {
          title: "Inward DC",
          description: "Verify and book inward delivery challans for vendor and site receipts.",
          route: "/admin/delivery-challans?type=inward",
          icon: "call_received",
          color: "bg-teal-50 text-teal-600 border-teal-100",
        },
      ],
    },
    {
      title: "Project & Master Settings",
      items: [
        {
          title: "Project Expenses",
          description: "Track project budgets, site-wise logistics costs, and engineering outlays.",
          route: "/admin/project-expenses",
          icon: "folder",
          color: "bg-violet-50 text-violet-600 border-violet-100",
        },
        {
          title: "Product Mapping",
          description: "Link vendor and customer part numbers to standard product master catalogs.",
          route: "/admin/product-mapping",
          icon: "grid_view",
          color: "bg-pink-50 text-pink-600 border-pink-100",
        },
      ],
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="border-b border-slate-100 pb-5">
        <span className="font-label-caps text-[11px] text-primary uppercase font-bold tracking-widest mb-1.5 block">
          ADMIN WORKSPACE
        </span>
        <h1 className="font-headline-lg text-3xl font-extrabold text-slate-900 tracking-tight">
          Sales &amp; Purchase Management
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Select a finance or logistics workflow below to draft invoices, POs, and challans.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.title} className="space-y-4">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-l-4 border-primary pl-3">
              {section.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item) => (
                <button
                  key={item.route}
                  onClick={() => navigate(item.route)}
                  className="group bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary/45 transition-all text-left flex items-start gap-4 active:scale-[0.98] w-full"
                >
                  {/* Left Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border shrink-0 ${item.color}`}>
                    <span className="material-symbols-outlined text-2xl">{item.icon}</span>
                  </div>

                  {/* Text Contents */}
                  <div className="flex-1 min-w-0 pr-2">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {/* Badge or Right Arrow */}
                  <div className="flex flex-col items-end justify-between self-stretch shrink-0">
                    {item.badge ? (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.badgeColor}`}>
                        {item.badge}
                      </span>
                    ) : (
                      <span className="material-symbols-outlined text-slate-300 text-sm group-hover:text-primary transition-colors group-hover:translate-x-0.5 duration-200">
                        arrow_forward
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
